// ModalTagsMultiples.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
  Divider,
  Pagination
} from "@mui/material";
import Swal from "sweetalert2";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.hmsaa.com.mx:5001";
const PAGE_SIZE = 10;

export default function ModalTagsMultiples({
  open,
  onClose,
  productos = [],
  onComplete
}) {
  const [tagsSistema, setTagsSistema] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);

  // asignación por producto: { [id_producto]: tagObj|null }
  const [asignaciones, setAsignaciones] = useState({});

  // filtros por producto: { [id_producto]: { texto: "", depto: "" } }
  const [filtros, setFiltros] = useState({});

  // paginación por producto: { [id_producto]: number }
  const [page, setPage] = useState({});

  const [loading, setLoading] = useState(false);

  // ==========================================================
  // INIT (igual que el modal simple)
  // ==========================================================
  useEffect(() => {
    if (!open) return;

    // reinicia estados por producto
    const initAsign = {};
    const initFiltros = {};
    const initPage = {};

    (productos || []).forEach(p => {
      initAsign[p.id_producto] = null;
      initFiltros[p.id_producto] = { texto: "", depto: "" };
      initPage[p.id_producto] = 1;
    });

    setAsignaciones(initAsign);
    setFiltros(initFiltros);
    setPage(initPage);

    // carga tags + departamentos (sin JWT, igual al modal que funciona)
    fetch(`${API_BASE}/api/tags?disponibles=true`)
      .then(r => r.json())
      .then(data => setTagsSistema(Array.isArray(data) ? data : (data || [])))
      .catch(() => setTagsSistema([]));

    fetch(`${API_BASE}/api/departamentos`)
      .then(r => r.json())
      .then(data => setDepartamentos(Array.isArray(data) ? data : (data || [])))
      .catch(() => setDepartamentos([]));
  }, [open, productos]);

  // ==========================================================
  // FILTRADO POR PRODUCTO (igual lógica que el simple)
  // ==========================================================
  const getTagsFiltrados = (prodId) => {
    const f = filtros[prodId] || { texto: "", depto: "" };

    return (tagsSistema || []).filter(t => {
      if (t.asignado) return false;

      if (f.depto && t.departamento !== f.depto) return false;

      if (f.texto) {
        const txt = f.texto.toLowerCase();
        const codigo = String(t.codigo || "").toLowerCase();
        const tipo = (codigo.split("-")[0] || "").toLowerCase();
        if (!codigo.includes(txt) && !tipo.includes(txt)) return false;
      }

      return true;
    });
  };

  // ==========================================================
  // SELECT TAG PARA UN PRODUCTO
  // ==========================================================
  const selectTag = (prodId, tag) => {
    setAsignaciones(prev => ({ ...prev, [prodId]: tag }));
  };

  // ==========================================================
  // GUARDAR TODO
  // ==========================================================
  const handleSaveAll = async () => {
    const faltan = (productos || []).some(p => !asignaciones[p.id_producto]);
    if (faltan) {
      return Swal.fire("Atención", "Debes asignar un tag a cada producto", "warning");
    }

    setLoading(true);
    try {
      await Promise.all(
        (productos || []).map(async (p) => {
          const tag = asignaciones[p.id_producto];

          const res = await fetch(
            `${API_BASE}/api/productos/${p.id_producto}/tags`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id_tag: tag.id_tag })
            }
          );

          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data?.error || `Error asignando tag a producto ${p.id_producto}`);
          return data;
        })
      );

      Swal.fire("Éxito", "Tags asignados correctamente", "success");
      await onComplete?.();
      onClose?.();
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Asignar Tags a los Productos</DialogTitle>

      <DialogContent dividers>
        {(productos || []).map(prod => {
          const prodId = prod.id_producto;

          const tagsFiltrados = getTagsFiltrados(prodId);

          const totalPages = Math.ceil(tagsFiltrados.length / PAGE_SIZE) || 1;
          const currentPage = page[prodId] || 1;

          const pageItems = tagsFiltrados.slice(
            (currentPage - 1) * PAGE_SIZE,
            currentPage * PAGE_SIZE
          );

          const selectedTag = asignaciones[prodId];

          return (
            <Box key={prodId} mb={3} p={2} border="1px solid #ddd" borderRadius={2}>
              <Typography fontWeight="bold" mb={1}>
                Serie: {prod.numero_serie} {prod.descripcion ? `— ${prod.descripcion}` : ""}
              </Typography>

              {/* FILTROS */}
              <Box display="flex" gap={1} mb={1}>
                <TextField
                  size="small"
                  label="Buscar por código o tipo (ej. TI)"
                  value={filtros[prodId]?.texto || ""}
                  onChange={e => {
                    const value = e.target.value;
                    setFiltros(prev => ({
                      ...prev,
                      [prodId]: { ...(prev[prodId] || {}), texto: value }
                    }));
                    setPage(prev => ({ ...prev, [prodId]: 1 }));
                  }}
                  fullWidth
                />

                <TextField
                  select
                  size="small"
                  label="Departamento"
                  value={filtros[prodId]?.depto || ""}
                  onChange={e => {
                    const value = e.target.value;
                    setFiltros(prev => ({
                      ...prev,
                      [prodId]: { ...(prev[prodId] || {}), depto: value }
                    }));
                    setPage(prev => ({ ...prev, [prodId]: 1 }));
                  }}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {(departamentos || []).map(d => (
                    <MenuItem key={d.id_departamento} value={d.nombre}>
                      {d.nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Divider sx={{ mb: 1 }} />

              {/* SELECTED */}
              {selectedTag && (
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Seleccionado: <b>{selectedTag.codigo}</b> — {selectedTag.departamento}
                </Typography>
              )}

              {/* LISTA TAGS */}
              <Box border="1px solid #eee" borderRadius={1}>
                {pageItems.map(tag => {
                  const selected = selectedTag?.id_tag === tag.id_tag;

                  return (
                    <Box
                      key={tag.id_tag}
                      p={1.2}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: selected ? "#e3f2fd" : "transparent",
                        "&:hover": { backgroundColor: "#f5f7fa" }
                      }}
                      onClick={() => selectTag(prodId, tag)}
                    >
                      <Typography variant="body2">
                        <b>{tag.codigo}</b> — {tag.departamento}
                      </Typography>
                    </Box>
                  );
                })}

                {pageItems.length === 0 && (
                  <Typography p={2} align="center" color="text.secondary">
                    No hay tags disponibles
                  </Typography>
                )}
              </Box>

              {/* PAGINACIÓN */}
              {totalPages > 1 && (
                <Box mt={1} display="flex" justifyContent="center">
                  <Pagination
                    size="small"
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, v) => setPage(prev => ({ ...prev, [prodId]: v }))}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSaveAll}
          disabled={loading || (productos || []).length === 0}
        >
          {loading ? "Guardando..." : "Guardar todos"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
